import React, { CSSProperties } from 'react';
import ReactDOM from 'react-dom';

import styles from './InputSelect.module.css';

import { useState } from 'react';
import useClickOutside from '../../../hooks/useClickOutside';

import { OptionsSelect } from '../../../interfaces/Input';
import useTooltip from '../../../hooks/useTooltip';

interface Props {
    options: OptionsSelect[],
    value: string,
    label: string,
    error?: boolean,
    errorMsg?: string,
    autocomplete?: boolean,
    simpleStyles?: boolean,
    disabled?: boolean,
    width?: number | string
    style?: CSSProperties
    disabledOptions?: (string | number)[]
    required?: boolean
    onTextInputChange?: (text: string) => void,
    onChange?: (option: OptionsSelect) => void
    onBlur?: () => void
}

const InputSelect: React.FC<Props> = ({
    options, value, label, error, errorMsg, autocomplete = false,
    simpleStyles, style, disabledOptions, width, required, onTextInputChange, onChange, onBlur
}) => {

    const [showOptions, setShowOptions] = useState(false);
    const [autoCompleteText, setAutoCompleteText] = useState(value || '');
    const [selectedId, setSelectedId] = useState<number | string | null>(null);

    const handleClickSelect = () => {
        setShowOptions(isShowOptions => !isShowOptions);
    };

    const handleClickOption = (option: OptionsSelect) => {
        setSelectedId(option.id);
        if (autocomplete) setAutoCompleteText((option.name));
        setShowOptions(false);
        if (onChange) onChange(option);
        if (onTextInputChange) onTextInputChange(option.name);
    };

    const handleChangeText = (evt: React.ChangeEvent<HTMLInputElement>) => {
        if (autocomplete) setAutoCompleteText(evt.target.value);
        if (onTextInputChange) onTextInputChange(evt.target.value);
    };

    const selectTextClass = autocomplete ? styles.selectInputText : '';

    const filteredOptions = !autocomplete ? options : options.filter(option => option.name.toUpperCase().includes(autoCompleteText.toUpperCase()));

    const setFalse = () => setShowOptions(false);




    const getStylePos = (pos: DOMRect) => ({ top: `${pos.bottom + 5}px`, left: `${pos.left}px`, width: pos.width });

    const { tooltipRef, getStyles } = useTooltip(getStylePos);

    const wrapperRef = useClickOutside(setFalse, tooltipRef);

    return (
        <div className={styles.inputWrapper} ref={tooltipRef} style={{ width, maxWidth: width }}>
            <div className={`${styles.select} ${showOptions ? styles.selectShowOptions : ''}`} >
                <div
                    onClick={!autocomplete ? handleClickSelect : undefined}
                    onFocus={autocomplete ? () => setAutoCompleteText('') : undefined}
                    className={`${simpleStyles ? styles.simpleStyles : ''} ${styles.selectText} ${selectTextClass} ${error ? styles.inputError : ''}`}
                    tabIndex={0}
                    data-testid='inputSelectContainer'
                    style={style}
                >
                    {autocomplete ?
                        <input
                            onFocus={() => setShowOptions(true)}
                            onChange={handleChangeText}
                            type='text'
                            className={styles.inputText}
                            value={autoCompleteText}></input>
                        :
                        <p>{value}</p>
                    }
                </div>
                {!simpleStyles && <label className={styles.selectLabel}>{label} {required && <span>*</span>}</label>}
                {showOptions && ReactDOM.createPortal(
                    <div
                        className={styles.optionsContainer}
                        style={{
                            ...getStyles(),
                        }}
                        ref={wrapperRef}
                    >
                        {showOptions && filteredOptions.map((option, index) =>
                            <input
                                onBlur={onBlur}
                                onClick={() => handleClickOption(option)}
                                type={'button'}
                                value={option.name}
                                className={`${styles.selectOption} ${selectedId === option.id ? styles.activeOption : null}`}
                                key={index}
                                disabled={disabledOptions?.some(id => option.id === id)}
                            />
                        )}
                    </div>, document.getElementById('tooltip') as HTMLElement,
                )}
            </div>
            {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}
        </div>
    );
};

export default InputSelect;